import React from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helper?: string;
  icon?: React.ReactNode;
  multiline?: boolean;
  rows?: number;
  cols?: number;
}

export default function Input({
  label,
  error,
  helper,
  icon,
  multiline,
  className = '',
  ...props
}: InputProps): React.JSX.Element {
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = props.type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : props.type;

  const baseClasses = `
    w-full bg-white border rounded-xl px-4 py-3 text-sm text-slate-900
    placeholder:text-slate-400
    focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500
    transition-all duration-200
    disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-200 hover:border-slate-300'}
    ${icon ? 'pl-11' : ''}
    ${isPassword ? 'pr-11' : ''}
  `;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700">
          {label}
          {props.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            {icon}
          </div>
        )}
        {multiline ? (
          <textarea className={baseClasses} {...(props as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} />
        ) : (
          <input
            className={baseClasses}
            type={inputType}
            {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
          />
        )}
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {error && (
        <p className="flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
      {helper && !error && <p className="text-xs text-slate-500">{helper}</p>}
    </div>
  );
}
